/**
 * MenuOrderListPage — anchor port (devourplay-anchor/menu-orders.html).
 *
 * /menu-orders surface. Order history for the authenticated user:
 * gift-card claims (LootShop digital), Fuel loads (when wired), and
 * physical merchant deliveries. Renders a filter strip + a grid of
 * order rows that each expand in place to a receipt panel with
 * gift-card redemption links + totals.
 *
 * Status semantics (matches design system):
 *   amber = pending / in-flight / processing
 *   green = completed / success
 *   gray  = cancelled / refunded
 *   red is RESERVED for brand / primary CTA — not for status.
 */

import { ReactElement, ReactNode, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    MenuOrder,
    SortOrder,
    GetMenuOrdersOwnSort,
    MenuOrderStatus,
    Token,
    Transaction,
    TransactionSubject,
    TransactionType,
} from "@devour/client";
import Paginator, { defaultFrontendPagination, FrontendPagination } from "@/components/paginator/Paginator";
import { IStore } from "@/redux/defaultStore";
import MenuOrderListCard from "../../components/menuOrder/MenuOrderListCard";
import FuelLoadListCard from "../../components/menuOrder/FuelLoadListCard";
import MenuOrderListPageSkeleton from "../../components/skeletons/MenuOrderListPage/MenuOrderListPageSkeleton";
import { useGetMenuOrdersOwn } from "@/hooks/useGetMenuOrdersOwn";
import { useGetTransactionsPaginated } from "@/hooks/useGetTransactionsPaginated";
import { useGetFuelBalance } from "@/hooks/useGetFuelBalance";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import DevourTopNav from "@/components/DevourTopNav";
import MenuOrderPlaceholder from "./MenuOrderPlaceholder";
import { toggleChatOpen } from "@/redux/meta/metaActions";
import { FaQuestionCircle } from "react-icons/fa";

export const nonReceivedList: Array<MenuOrderStatus> = [
    MenuOrderStatus.CART,
    MenuOrderStatus.CHECKOUT,
    MenuOrderStatus.PAYMENT,
    MenuOrderStatus.PAID,
    MenuOrderStatus.PROCESSING,
];

export const processingList: Array<MenuOrderStatus> = [
    MenuOrderStatus.PAID,
    MenuOrderStatus.PROCESSING,
    // Part of a mixed LootBox + gift-card order delivered, rest still pending — still in-flight.
    MenuOrderStatus.PARTIALLY_DELIVERED,
    MenuOrderStatus.RECEIVED,
    MenuOrderStatus.DELIVERY_SERVICE,
    MenuOrderStatus.DRIVER_PICKUP,
    MenuOrderStatus.DRIVER_ASSIGNED,
    MenuOrderStatus.OUT_FOR_DELIVERY,
    MenuOrderStatus.DRIVER_AT_DESTINATION,
];

export const ongoingStatusesList: Array<MenuOrderStatus> = [
    MenuOrderStatus.DELIVERY_SERVICE,
    MenuOrderStatus.DRIVER_PICKUP,
    MenuOrderStatus.OUT_FOR_DELIVERY,
    MenuOrderStatus.DRIVER_ASSIGNED,
    MenuOrderStatus.DRIVER_AT_DESTINATION,
];

export const completedList: Array<MenuOrderStatus> = [
    MenuOrderStatus.DELIVERED,
    MenuOrderStatus.CUSTOMER_PICKUP,
    MenuOrderStatus.CUSTOMER_PICKED_UP,
    MenuOrderStatus.REFUNDED,
    MenuOrderStatus.AUTO_REFUNDED,
    MenuOrderStatus.RESOLVED,
    MenuOrderStatus.SHIPPED,
];

export const cancelledList: Array<MenuOrderStatus> = [
    MenuOrderStatus.ERROR_DEVOUR,
    MenuOrderStatus.ERROR_RESTAURANT,
    MenuOrderStatus.ERROR_DELIVERY,
    MenuOrderStatus.ERROR_PAYMENT,
    MenuOrderStatus.ERROR_DEVOURIQ,
    MenuOrderStatus.SYSTEM_CANCELLED,
    MenuOrderStatus.CUSTOMER_CANCELLED,
];

export const cancelledOnlyList: Array<MenuOrderStatus> = [
    MenuOrderStatus.SYSTEM_CANCELLED,
    MenuOrderStatus.CUSTOMER_CANCELLED,
    MenuOrderStatus.REFUNDED,
    MenuOrderStatus.AUTO_REFUNDED,
];

export const notSubmittedList: Array<MenuOrderStatus> = [
    MenuOrderStatus.ERROR_DEVOUR,
    MenuOrderStatus.ERROR_RESTAURANT,
    MenuOrderStatus.ERROR_PAYMENT,
    MenuOrderStatus.ERROR_DELIVERY,
    MenuOrderStatus.ERROR_DEVOURIQ,
];

export const erroredList: Array<MenuOrderStatus> = [
    ...cancelledOnlyList,
    ...notSubmittedList,
];

export const menuOrderFinalizedStatuses: Array<MenuOrderStatus> = [
    ...completedList,
    ...cancelledList,
];

export const allVisibleStatuses: Array<MenuOrderStatus> = [
    ...processingList,
    ...completedList,
];

/*
 * Legacy export retained for back-compat with consumers that still
 * call this helper (MenuOrderSuccess, etc). Returns the same 3-bucket
 * label + a legacy "the legacy component" string. New anchor surfaces
 * derive their own pill color from the bucket directly.
 */
export function getOrderStatusBucket(order: MenuOrder): "progress" | "complete" | "cancel" {
    if (completedList.includes(order.status)) return "complete";
    if (cancelledList.includes(order.status)) return "cancel";
    return "progress";
}

// Kept for back-compat with consumers that reference the old "Tag" prop shape.
export function getOrderStatusTag(order: MenuOrder): { name: string; color: "success" | "warning" | "gray-light" } {
    const bucket = getOrderStatusBucket(order);
    if (bucket === "complete") return { name: "Completed", color: "success" };
    if (bucket === "cancel") return { name: "Cancelled", color: "gray-light" };
    return { name: "Processing", color: "warning" };
}

type FilterKey = "all" | "progress" | "complete" | "cancel";

/*
 * Discriminated union of row types rendered in the Order History list.
 * Each row carries a `createdAt` timestamp + a `bucket` so the
 * single sorted/filtered pipeline downstream doesn't have to type-
 * narrow on payload shape repeatedly.
 */
type OrderHistoryRow =
    | { kind: "order"; id: string; createdAt: number | Date; bucket: ReturnType<typeof getOrderStatusBucket>; order: MenuOrder }
    | { kind: "fuel"; id: string; createdAt: number | Date; bucket: "complete"; transaction: Transaction };

/*
 * Subjects that represent a USER-INITIATED Fuel Load (the kind of
 * row that belongs in Order History). Excludes reward/quest-derived
 * Fuel credits — those aren't "orders" the user placed and would
 * spam the history with system noise.
 */
const FUEL_LOAD_SUBJECTS: ReadonlySet<TransactionSubject> = new Set([
    TransactionSubject.FIAT_PURCHASE_FUEL,
    TransactionSubject.XP_PURCHASE_FUEL,
]);

function MenuOrderListPage(): ReactElement {
    const [frontendPagination, setFrontendPagination] = useState<FrontendPagination>(defaultFrontendPagination);
    const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
    const dispatch = useDispatch();
    const fullToken = useSelector<IStore>(state => state.authStore.fullToken);
    const { data: userProfile } = useGetUserProfile(fullToken as Token);
    const userId = userProfile?.user?.id;

    const { data: menuOrdersOwn } = useGetMenuOrdersOwn(fullToken as Token, {
        sort: GetMenuOrdersOwnSort.createdAt,
        order: SortOrder.desc,
        limit: frontendPagination.limit,
        offset: frontendPagination.offset,
        status: allVisibleStatuses,
    });

    /*
     * Fuel Load transactions live in the Transaction ledger, not the
     * MenuOrder collection. Pull them separately and merge into the
     * unified Order History list. Pagination on the Transaction side
     * is coarse — we fetch a healthy first page and rely on the
     * MenuOrder side's paginator for "load more" navigation; a
     * dedicated merged paginator can land later if the row count
     * outgrows this approach.
     */
    const { data: transactionsData } = useGetTransactionsPaginated(
        fullToken as Token,
        userId,
        { limit: 50, offset: 0 },
        !userId,
    );
    /*
     * Canonical Fuel→USD rate (shared cache) for valuing Fuel-load rows — same
     * rate the balance uses everywhere, so a load's USD never diverges from the
     * balance's. The paginated query above is only for the transaction list.
     */
    const { data: fuelBalanceData } = useGetFuelBalance(fullToken as Token, userId);

    const fuelLoadRows = useMemo<OrderHistoryRow[]>(() => {
        /*
         * GetTransactionsPaginatedResponse.transactions is the
         * TransactionsPaginated wrapper; the actual Transaction[]
         * lives at `.transactions.data`. Unwrap defensively.
         */
        const txs = transactionsData?.transactions?.data ?? [];
        return txs
            .filter(t => t.type === TransactionType.CREDIT && FUEL_LOAD_SUBJECTS.has(t.subject))
            .map(t => ({
                kind: "fuel" as const,
                id: t.id,
                createdAt: t.createdAt,
                bucket: "complete" as const,
                transaction: t,
            }));
    }, [transactionsData]);

    const orderRows = useMemo<OrderHistoryRow[]>(() => {
        const orders = menuOrdersOwn?.menuOrders ?? [];
        return orders.map(o => ({
            kind: "order" as const,
            id: o.id,
            createdAt: o.createdAt,
            bucket: getOrderStatusBucket(o),
            order: o,
        }));
    }, [menuOrdersOwn?.menuOrders]);

    /*
     * Sort: in-progress on top (active concern), then everything else
     * newest-first across both row types so a recent Fuel Load
     * interleaves naturally with a recent gift-card claim.
     */
    const sortedRows = useMemo(() => {
        const merged: OrderHistoryRow[] = [...orderRows, ...fuelLoadRows];
        return merged.sort((a, b) => {
            const aIsProgress = a.bucket === "progress";
            const bIsProgress = b.bucket === "progress";
            if (aIsProgress && !bIsProgress) return -1;
            if (!aIsProgress && bIsProgress) return 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    }, [orderRows, fuelLoadRows]);

    const filterCounts = useMemo(() => {
        const counts = { all: 0, progress: 0, complete: 0, cancel: 0 };
        for (const r of sortedRows) {
            counts.all += 1;
            counts[r.bucket] += 1;
        }
        return counts;
    }, [sortedRows]);

    const filteredRows = useMemo(() => {
        if (activeFilter === "all") return sortedRows;
        return sortedRows.filter(r => r.bucket === activeFilter);
    }, [sortedRows, activeFilter]);

    function renderRow(row: OrderHistoryRow): ReactNode {
        if (row.kind === "fuel") {
            return <FuelLoadListCard key={`fuel-${row.id}`} transaction={row.transaction} fuelPricePerUsd={fuelBalanceData?.fuelPricePerUsd} />;
        }
        return <MenuOrderListCard key={`order-${row.id}`} order={row.order} />;
    }

    function toggleHelp(): void {
        dispatch(toggleChatOpen(true));
    }

    const tabs: Array<{ key: FilterKey; label: string }> = [
        { key: "all", label: "All" },
        { key: "progress", label: "In Progress" },
        { key: "complete", label: "Completed" },
        { key: "cancel", label: "Cancelled" },
    ];

    return (
        <div className="dp-orders-page">
            <DevourTopNav text="Order History" />

            <div className="dp-orders-page__container">

                {/* §01 Page header */}
                <header className="dp-orders-hdr">
                    <div className="dp-orders-hdr__row">
                        <div className="dp-orders-hdr__body">
                            <div className="dp-orders-hdr__eyebrow">
                                <strong>/ STASH</strong>
                                <span>·</span>
                                <span>ORDER HISTORY</span>
                            </div>
                            <h1 className="dp-orders-hdr__title">Order History</h1>
                            <p className="dp-orders-hdr__sub">
                                Every gift card, every Fuel load, every reorder. Status, totals, and
                                redemption links live here.
                            </p>
                        </div>
                        <div className="dp-orders-hdr__actions">
                            <button type="button" className="dp-orders-btn-ghost" onClick={toggleHelp}>
                                <FaQuestionCircle /> Order Help
                            </button>
                        </div>
                    </div>
                </header>

                {/* §02 Filter strip */}
                <section className="dp-orders-filter">
                    <div className="dp-orders-filter__tabs">
                        {tabs.map(t =>
                            <button
                                key={t.key}
                                type="button"
                                className={`dp-orders-filter__tab${activeFilter === t.key ? " is-active" : ""}`}
                                onClick={() => setActiveFilter(t.key)}
                            >
                                {t.label}
                                <span className="dp-orders-filter__tab-count">
                                    {filterCounts[t.key]}
                                </span>
                            </button>)}
                    </div>
                    <div className="dp-orders-filter__meta">
                        Sort <strong>Newest first</strong>
                    </div>
                </section>

                {/* §03 Orders list — merged MenuOrders + Fuel Loads */}
                {menuOrdersOwn
                    ? <section className="dp-orders-list">
                        {filteredRows.length > 0
                            ? <>
                                {filteredRows.map(renderRow)}
                                {menuOrdersOwn.paginationInfo && <Paginator
                                    {...frontendPagination}
                                    {...menuOrdersOwn.paginationInfo}
                                    onPaginationChange={setFrontendPagination}
                                    showPaginatorLimit={true}
                                />}
                            </>
                            : <MenuOrderPlaceholder />}
                    </section>
                    : <MenuOrderListPageSkeleton />}

            </div>
        </div>
    );
}

export default MenuOrderListPage;
